import { useMemo } from 'react';
import { format, parseISO, isWeekend } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import type { ReportUser, AttendanceEntry } from '../../types/report';

/**
 * The distance reported for a day is the modified value when the backend
 * has an OSRM-corrected total, otherwise the raw haversine sum. `null` /
 * absent modified value falls through to raw so old responses (before
 * the OSRM columns shipped) still render.
 */
function effectiveMeters(entry: AttendanceEntry): number {
  return entry.total_modified_distance_meters ?? entry.total_distance_meters ?? 0;
}

/**
 * A day is "corrected" when the modified total is present AND
 * meaningfully different from the raw total — using `>= 1 m` as the
 * significance threshold so floating-point noise doesn't flip the flag.
 */
function isCorrected(entry: AttendanceEntry): boolean {
  const modified = entry.total_modified_distance_meters;
  if (modified === null || modified === undefined) return false;
  const raw = entry.total_distance_meters ?? 0;
  return Math.abs(modified - raw) >= 1;
}

interface ReportGridProps {
  users: ReportUser[];
  dates: Date[];
  isLoading: boolean;
}

/** Format seconds to compact duration */
function fmtDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** Format meters to compact distance */
function fmtMeters(meters: number): string {
  if (!meters || meters <= 0) return '—';
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

/** Format ISO time string to HH:mm */
function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'HH:mm');
  } catch {
    return '—';
  }
}

/** Build attendance map for a user */
function buildAttendanceMap(attendance: AttendanceEntry[]): Map<string, AttendanceEntry> {
  const map = new Map<string, AttendanceEntry>();
  for (const e of attendance) map.set(e.date, e);
  return map;
}

/** Compute summary for a user */
function computeSummary(attendance: AttendanceEntry[], totalDays: number) {
  let daysPresent = 0;
  let totalMeters = 0;
  let totalSeconds = 0;
  let correctedDays = 0;
  for (const e of attendance) {
    daysPresent++;
    totalMeters += effectiveMeters(e);
    totalSeconds += e.total_time_active_seconds;
    if (isCorrected(e)) correctedDays++;
  }
  return { daysPresent, totalDays, totalMeters, totalSeconds, correctedDays };
}

/** Build a hover tooltip explaining a corrected day. */
function correctionTooltip(entry: AttendanceEntry): string {
  const raw = entry.total_distance_meters ?? 0;
  const modified = entry.total_modified_distance_meters ?? raw;
  const rawKm = raw >= 1000 ? `${(raw / 1000).toFixed(2)} km` : `${Math.round(raw)} m`;
  const modKm = modified >= 1000 ? `${(modified / 1000).toFixed(2)} km` : `${Math.round(modified)} m`;
  const parts = [
    `Path-corrected distance`,
    `Raw: ${rawKm}`,
    `Corrected: ${modKm}`,
  ];
  if (entry.updated_by) parts.push(`By: ${entry.updated_by}`);
  if (entry.updated_at) {
    try {
      parts.push(`On: ${format(parseISO(entry.updated_at), 'dd MMM yyyy HH:mm')}`);
    } catch {
      // ignore parse errors — the badge itself is still meaningful
    }
  }
  return parts.join('\n');
}

export const ReportGrid: React.FC<ReportGridProps> = ({ users, dates, isLoading }) => {
  const dateStrings = useMemo(() => dates.map((d) => format(d, 'yyyy-MM-dd')), [dates]);

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-8 h-8 border-2 border-rgf-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-rgf-gray-600">Loading report data...</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-rgf-gray-500">No data found for the selected filters.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-rgf-gray-200 rounded-lg">
      <table className="border-collapse" style={{ minWidth: '100%' }}>
        <thead>
          <tr>
            {/* Sticky user info header */}
            <th
              className="sticky left-0 z-20 bg-rgf-gray-100 border-b border-r-2 border-rgf-gray-300 px-4 py-3 text-left text-xs font-semibold text-rgf-gray-600 uppercase tracking-wider"
              style={{ minWidth: 300 }}
            >
              User
            </th>

            {/* Date column headers */}
            {dates.map((date, i) => {
              const weekend = isWeekend(date);
              return (
                <th
                  key={dateStrings[i]}
                  className={`border-b border-r border-rgf-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                    weekend
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-rgf-gray-100 text-rgf-gray-600'
                  }`}
                  style={{ minWidth: 130 }}
                >
                  <div>{format(date, 'EEE')}</div>
                  <div className="text-[11px] font-medium normal-case">
                    {format(date, 'dd MMM')}
                  </div>
                </th>
              );
            })}

            {/* Sticky summary header */}
            <th
              className="sticky right-0 z-20 bg-blue-100 border-b border-l-2 border-blue-300 px-4 py-3 text-center text-xs font-semibold text-blue-700 uppercase tracking-wider"
              style={{ minWidth: 140 }}
            >
              Summary
            </th>
          </tr>
        </thead>

        <tbody>
          {users.map((user) => {
            const aMap = buildAttendanceMap(user.attendance);
            const summary = computeSummary(user.attendance, dates.length);

            return (
              <tr key={user.id} className="border-b border-rgf-gray-100">
                {/* Sticky user info cell */}
                <td
                  className="sticky left-0 z-10 bg-white border-r-2 border-rgf-gray-300 px-4 py-3"
                  style={{ maxWidth: 300, width: 300 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 bg-rgf-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-rgf-primary-700">
                        {user.name
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((w) => w[0])
                          .join('')
                          .toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 overflow-hidden">
                      <div
                        className="text-sm font-medium text-rgf-gray-900 truncate"
                        title={user.employee_id ? `${user.name} (${user.employee_id})` : user.name}
                      >
                        {user.name}
                        {user.employee_id && (
                          <span className="ml-1 text-rgf-gray-500 font-normal">
                            ({user.employee_id})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="badge badge-info text-[10px] px-1.5 py-0 flex-shrink-0">
                          {user.program_id}
                        </span>
                        <span className="text-[10px] text-rgf-gray-500 truncate" title={user.role}>
                          {user.role.length > 25
                            ? user.role.slice(0, 25) + '...'
                            : user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                {/* Date cells */}
                {dateStrings.map((ds, i) => {
                  const entry = aMap.get(ds);
                  const weekend = isWeekend(dates[i]);

                  if (!entry) {
                    return (
                      <td
                        key={ds}
                        className={`border-r border-rgf-gray-100 px-3 py-3 text-center align-middle ${
                          weekend ? 'bg-gray-50' : 'bg-red-50'
                        }`}
                      >
                        <span className="text-xs text-gray-400">Absent</span>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={ds}
                      className={`border-r border-rgf-gray-100 px-3 py-2 align-top ${
                        weekend ? 'bg-green-50/50' : 'bg-green-50'
                      }`}
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>
                          <span className="text-[11px] font-medium text-green-800">
                            Present
                          </span>
                        </div>
                        <div className="text-[11px] text-rgf-gray-700">
                          <span className="text-gray-500">In:</span>{' '}
                          <span className="font-medium">{fmtTime(entry.start_time)}</span>
                        </div>
                        <div className="text-[11px] text-rgf-gray-700">
                          <span className="text-gray-500">Out:</span>{' '}
                          <span className="font-medium">{fmtTime(entry.end_time)}</span>
                        </div>
                        <div className="text-[11px] text-rgf-gray-600">
                          {fmtDuration(entry.total_time_active_seconds)}
                        </div>
                        <div
                          className="text-[11px] text-rgf-gray-600 flex items-center gap-1"
                          title={
                            isCorrected(entry) ? correctionTooltip(entry) : undefined
                          }
                        >
                          {fmtMeters(effectiveMeters(entry))}
                          {isCorrected(entry) && (
                            <CheckCircle2
                              className="h-3 w-3 text-emerald-600 flex-shrink-0"
                              aria-label="Path-corrected distance"
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}

                {/* Sticky summary cell */}
                <td className="sticky right-0 z-10 bg-blue-50 border-l-2 border-blue-200 px-4 py-3 text-center">
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-blue-800">
                      {summary.daysPresent}/{summary.totalDays} days
                    </div>
                    <div className="text-[11px] text-blue-700 flex items-center justify-center gap-1">
                      {fmtMeters(summary.totalMeters)}
                      {summary.correctedDays > 0 && (
                        <span
                          title={`${summary.correctedDays} day${
                            summary.correctedDays === 1 ? '' : 's'
                          } include path-corrected distance`}
                        >
                          <CheckCircle2
                            className="h-3 w-3 text-emerald-600 flex-shrink-0"
                            aria-label={`${summary.correctedDays} day${
                              summary.correctedDays === 1 ? '' : 's'
                            } include path-corrected distance`}
                          />
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-blue-700">
                      {fmtDuration(summary.totalSeconds)}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
